import React from 'react'
import { Box, Skeleton, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Bar, Line } from 'react-chartjs-2'
import './chartKit'
import { C, cardSx, tabular } from './tokens'
import { pct } from './format'

/**
 * One headline number. Label on top; the value with its sparkline beside it;
 * the change underneath, as an amount and a percentage ("▼ $72.40 (50%)").
 * About 120px tall, and the same height while loading so nothing jumps.
 */

function Delta({ cur, prev, fmtAbs, label, points = false }) {
  if (!label) return <Box sx={{ height: 22 }} />
  const c = Number(cur) || 0
  const p = prev == null ? null : Number(prev)
  if (p == null) return <Box sx={{ height: 22 }} />
  const diff = c - p
  const flat = Math.abs(diff) < 0.005
  const isNew = p === 0 && c > 0
  const good = diff > 0
  const color = flat ? C.textMuted : good ? C.upText : C.downText
  const bg = flat ? '#F3F4F6' : good ? C.upSoft : C.downSoft
  let chip
  if (flat) chip = 'No change'
  else if (isNew) chip = `▲ ${fmtAbs(diff)} · New`
  else chip = `${good ? '▲' : '▼'} ${fmtAbs(Math.abs(diff))}${points ? '' : ` (${pct((diff / Math.abs(p)) * 100)})`}`
  const spoken = flat ? 'No change' : `${good ? 'Up' : 'Down'} ${fmtAbs(Math.abs(diff))}${isNew || points ? '' : `, ${pct((diff / Math.abs(p)) * 100)}`}`
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 0.75, rowGap: 0.25, minHeight: 22 }}>
      <Box component="span" aria-label={`${spoken} ${label}`}
        sx={{ ...tabular, fontSize: 12, fontWeight: 600, color, bgcolor: bg, px: 0.75, py: 0.25, borderRadius: '6px', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
        {chip}
      </Box>
      <Typography component="span" sx={{ fontSize: 12, color: C.textMuted }}>{label}</Typography>
    </Box>
  )
}

/* A line reads as a trend only with a few real points. Fewer than 4 non-zero
   days is a handful of spikes on a flat line, so it is drawn as mini bars. */
function Sparkline({ values, gaps }) {
  const data = values.map((v, i) => (gaps && gaps[i] ? null : v))
  const nonZero = data.filter(v => v != null && Number(v) !== 0).length
  const max = Math.max(0, ...data.filter(v => v != null).map(Number))
  const common = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    events: [],
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: { x: { display: false }, y: { display: false, min: 0, max: max > 0 ? max * 1.15 : 1 } },
    layout: { padding: { top: 3, bottom: 1, left: 1, right: 1 } }
  }
  if (nonZero < 4) {
    return (
      <Bar
        options={common}
        data={{
          labels: data.map((_, i) => i),
          datasets: [{ data, backgroundColor: C.brand, borderRadius: 1.5, barPercentage: 0.7, categoryPercentage: 0.9 }]
        }}
      />
    )
  }
  return (
    <Line
      options={common}
      data={{
        labels: data.map((_, i) => i),
        datasets: [{ data, borderColor: C.brand, backgroundColor: C.brandFill, borderWidth: 1.5, pointRadius: 0, tension: 0, fill: 'origin', spanGaps: false }]
      }}
    />
  )
}

export default function KpiCard({ label, hint, value, sub, warning, cur, prev, prevDisplay, fmtAbs, points, compareText, spark, gaps, loading, empty }) {
  const showSpark = !loading && !empty && spark && spark.length > 1
  const previousLine = compareText && Number(prev) > 0
    ? `${compareText.replace(/^vs /, '').replace(/^./, c => c.toUpperCase())}: ${prevDisplay}`
    : ''

  const labelEl = hint ? (
    <Tooltip title={hint} placement="top-start" arrow enterTouchDelay={0}>
      <Box tabIndex={0} aria-label={`${label}: ${hint}`}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-start', cursor: 'help', borderRadius: '4px', '&:focus-visible': { outline: `2px solid ${C.brandText}`, outlineOffset: 2 } }}>
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: C.textMuted, borderBottom: `1px dashed ${C.borderStrong}` }}>{label}</Typography>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: C.textMuted }} aria-hidden />
      </Box>
    </Tooltip>
  ) : (
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: C.textMuted }}>{label}</Typography>
  )

  return (
    <Box sx={{ ...cardSx, px: 2.5, py: 2, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 120, position: 'relative' }}>
      {labelEl}
      {warning ? <Box sx={{ position: 'absolute', top: 12, right: 12 }}>{warning}</Box> : null}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, minHeight: 38 }}>
        {loading
          ? <Skeleton variant="text" width="55%" sx={{ fontSize: 28 }} />
          : (
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
              <Typography sx={{ ...tabular, fontSize: 28, fontWeight: 600, color: C.text, lineHeight: 1.15, letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                {empty ? '—' : value}
              </Typography>
              {sub && !empty ? <Typography sx={{ ...tabular, fontSize: 12, color: C.textMuted, whiteSpace: 'nowrap' }}>{sub}</Typography> : null}
            </Box>
          )}
        <Box aria-hidden sx={{ width: 96, height: 32, flexShrink: 0 }}>
          {showSpark ? <Sparkline values={spark} gaps={gaps} /> : null}
        </Box>
      </Box>
      {loading
        ? <Skeleton variant="text" width="70%" sx={{ fontSize: 12 }} />
        : empty
          ? <Typography sx={{ ...tabular, fontSize: 12, color: C.textMuted, minHeight: 22, display: 'flex', alignItems: 'center' }}>{previousLine || 'No orders in this range'}</Typography>
          : <Delta cur={cur} prev={prev} fmtAbs={fmtAbs} points={points} label={compareText} />}
    </Box>
  )
}
